/**
 * SqueezeRadarStrip — Round 96n (Jul 3 2026)
 *
 * Compact horizontal strip on the Dashboard, slotted right under
 * MacroContextStrip. Shows the top 5 U.S. equities by squeeze-risk
 * score (SI-of-float × days-to-cover), refreshed nightly by the
 * `squeeze_radar_warmer` cron. Each chip is click-through to the
 * Stock Modal for the ticker.
 *
 * Renders nothing when:
 *   • The nightly snapshot has never been generated
 *   • Snapshot is stale by more than 48h (something is broken; hide
 *     it rather than show wrong data)
 *   • Zero candidates survived (very quiet market, or thin universe)
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Zap, HelpCircle, Flame, ArrowUp, ArrowDown, Lock } from 'lucide-react';
import usePriceStream from '@/hooks/usePriceStream';
import PlusUpsellModal from '@/components/PlusUpsellModal';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chip base styles per verdict. Each verdict now carries a border in
// its own color (rose/amber/sky/slate) to match the severity dot,
// giving each chip a self-consistent "personality" at a glance.
const VERDICT_STYLES = {
  Extreme: 'bg-sky-50 dark:bg-sky-950/50 text-slate-800 dark:text-slate-100 border-rose-400/70 dark:border-rose-500/50',
  Elevated: 'bg-sky-50 dark:bg-sky-950/50 text-slate-800 dark:text-slate-100 border-amber-400/70 dark:border-amber-500/50',
  Moderate: 'bg-sky-50 dark:bg-sky-950/50 text-slate-800 dark:text-slate-100 border-sky-400/70 dark:border-sky-500/50',
  Low: 'bg-sky-50 dark:bg-sky-950/50 text-slate-700 dark:text-slate-200 border-slate-400/70 dark:border-slate-500/50',
};

// Jul 7, 2026 — verdict severity dot. Rose/amber/sky/slate maps to
// Extreme/Elevated/Moderate/Low. Scannable at a glance without
// hovering; supersedes the previous text-color-only signal.
const VERDICT_DOTS = {
  Extreme: 'bg-rose-500',
  Elevated: 'bg-amber-500',
  Moderate: 'bg-sky-500',
  Low: 'bg-slate-400 dark:bg-slate-500',
};

// Jul 7, 2026 — "already moving" threshold. If a shorted stock is up
// (or down) more than this % intraday, we overlay a Flame icon on the
// chip to flag that the squeeze pressure is playing out RIGHT NOW —
// critical UX because the underlying SI% and days-to-cover data is
// only refreshed nightly by the `squeeze_radar_warmer` cron.
const SQUEEZE_MOVING_THRESHOLD_PCT = 15;

// Jul 7, 2026 — Ask the backend for the ACTUAL current market status
// (Massive `/v1/marketstatus/now`, 60s-cached server-side). Handles
// holidays, half-days, and the extended/early-hours edge cases the
// client can't reliably know about. Falls back to `true` on network
// error so the user still sees SOME UI rather than a frozen "Last
// Close" label at open. Polls once per minute.
function useMarketOpen() {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      axios.get(`${API}/market/status`, { timeout: 4000 })
        .then((r) => {
          if (cancelled) return;
          const label = (r?.data?.label || '').toLowerCase();
          setOpen(label === 'open');
        })
        .catch(() => { /* keep the previous value on transient errors */ });
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return open;
}

function fmtAgeShort(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const hrs = Math.max(0, (now - then) / 3_600_000);
  if (hrs < 1) return 'now';
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function SqueezeRadarStrip({ onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/dashboard/squeeze-radar`, { timeout: 5000, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data || !data.generated_at) return null;
  // Round 102d (Feb 2026) — Match the Streaks Extended row: show
  // ALL candidates the warmer stashed in the snapshot (up to the
  // backend's cap), horizontally scrollable when they overflow.
  // Previously capped at 5 for visual tidiness, which hid up to
  // 10 additional candidates that had already been computed.
  const rows = data.top || [];
  if (rows.length === 0) return null;
  const ageHrs = (Date.now() - new Date(data.generated_at).getTime()) / 3_600_000;
  if (ageHrs > 48) return null; // stale — hide rather than lie

  // Jul 7, 2026 — subscribe to the live-price SSE for every ticker in
  // the radar. Snapshot `r.price` is only the captured price at cron
  // time; users click through expecting to see the *current* market
  // price. Live diffs override the snapshot when they arrive.
  const radarTickers = rows.map((r) => String(r.ticker || '').toUpperCase()).filter(Boolean);
  return (
    <SqueezeRadarInner
      data={data}
      rows={rows}
      radarTickers={radarTickers}
      onOpenStock={onOpenStock}
    />
  );
}

function SqueezeRadarInner({ data, rows, radarTickers, onOpenStock }) {
  const swipeRef = useRef(null);
  const [swipeHint, updateSwipeHint] = useSwipeHint(swipeRef, [rows]);
  const { prices: livePrices } = usePriceStream(radarTickers);
  const marketOpen = useMarketOpen();
  // Round 102e (Feb 2026) — Sort-order toggle. Backend already ranks
  // rows by score DESC (Extreme→Low, per user's default). Toggle
  // reverses to Low→Extreme so users can see "safest-to-riskiest"
  // order without leaving the strip. Rank-preserving; no re-sort of
  // the underlying scores.
  const [sortDesc, setSortDesc] = useState(true);
  const [showUpsell, setShowUpsell] = useState(false);
  const orderedRows = sortDesc ? rows : [...rows].reverse();
  // Round P107b (Feb 2026) — External-focus subscription. The Capital
  // Quality Top 3 card and Top Dividend Movers card both render a
  // "→ ⚡ Squeeze Radar" pill next to any ticker that lives in the
  // radar snapshot. Clicking that pill dispatches a
  // `squeeze-radar:focus` window event with the ticker. This effect:
  //   1. Scrolls the whole strip into view (title first — block:start).
  //   2. After the smooth scroll settles, horizontally centers the
  //      matching chip inside the .overflow-x-auto scroller.
  //   3. Adds `.radar-chip-pulse` (orange keyframe outline) to the
  //      chip and installs one-shot dismissers: any click, wheel
  //      event, keyboard, or manual horizontal-scroll on the strip
  //      clears the pulse. This mirrors the "toast-like" attention
  //      grab used by the Filing Highlight system elsewhere.
  const rootRef = useRef(null);
  const dismissRef = useRef(null);  // holds the current dismiss callback
  useEffect(() => {
    const strip = rootRef.current;
    if (!strip) return undefined;

    const clearPulse = () => {
      if (dismissRef.current) {
        dismissRef.current();
        dismissRef.current = null;
      }
    };

    const focusTicker = (ticker) => {
      if (!ticker) return;
      clearPulse();  // if a previous pulse is still active, drop it

      // Step 1: center the strip vertically in the viewport (on both
      // mobile and desktop).
      //
      // Two earlier attempts didn't feel right:
      //   • `scrollIntoView({block: 'start'})` put the strip's top at
      //     y=0, HIDING it under the 135px sticky top nav.
      //   • `stripTop = headerH + GAP` felt right visually but users
      //     wanted the pulsing chip dead-center so it's obvious the
      //     click did something.
      // Center formula: shift viewport so the strip's midpoint aligns
      // with the viewport's midpoint. Clamp so the strip's top never
      // slides UNDER the sticky header (edge case on very short
      // viewports).
      //
      // Coordinate reminder: increasing scrollY moves content UP in
      // the viewport. So more scrollY = strip further UP; less
      // scrollY = strip further DOWN. To keep the strip below the
      // sticky header, scrollY must be AT MOST (strip_page_y − headerH)
      // — hence the Math.min upper clamp, not a Math.max.
      const stripRect = strip.getBoundingClientRect();
      const sticky = document.querySelector('header.sticky, header[class*="sticky"]');
      const headerH = sticky ? sticky.getBoundingClientRect().height : 0;
      const stripMid = stripRect.top + stripRect.height / 2;
      const vpMid = window.innerHeight / 2;
      const rawTarget = window.scrollY + (stripMid - vpMid);
      const maxSafe = window.scrollY + (stripRect.top - headerH - 8);
      const targetY = Math.max(0, Math.min(maxSafe, rawTarget));
      window.scrollTo({ top: targetY, behavior: 'smooth' });

      // Step 2 + 3 run after the smooth scroll finishes (~500ms is
      // typical; we use a slightly longer buffer so slow devices
      // don't miss it).
      const t = setTimeout(() => {
        const chip = strip.querySelector(`[data-testid="squeeze-radar-chip-${ticker}"]`);
        if (!chip) return;

        // Horizontal centering inside the strip's scroll container.
        const scroller = strip.querySelector('.overflow-x-auto');
        if (scroller) {
          const chipRect = chip.getBoundingClientRect();
          const scRect = scroller.getBoundingClientRect();
          const chipCenter = chipRect.left + chipRect.width / 2;
          const scrollerCenter = scRect.left + scRect.width / 2;
          const delta = chipCenter - scrollerCenter;
          scroller.scrollBy({ left: delta, behavior: 'smooth' });
        }

        chip.classList.add('radar-chip-pulse');

        // Install dismisser. Any of the following clears the pulse:
        //   • click anywhere on the page (including the chip / pill)
        //   • wheel/vertical scroll gesture
        //   • keydown
        //   • a *new* squeeze-radar:focus event (starts fresh below)
        //
        // P107d (Feb 2026) — Deliberately no `scroller.scroll` listener.
        // Every attempt to have "user horizontal-scrolls the strip"
        // dismiss the pulse triggered a race with our OWN centering
        // scrollBy (which keeps dispatching scroll events for up to
        // ~1s on slower devices) and killed the pulse instantly. A
        // grace window mitigated it partially but wasn't reliable
        // across browsers/CPUs. Dropping the scroller listener costs
        // us one edge-case dismiss condition but eliminates the
        // "pulse only works on the second click" bug entirely.
        const off = () => {
          chip.classList.remove('radar-chip-pulse');
          document.removeEventListener('click', off, true);
          document.removeEventListener('wheel', off, true);
          document.removeEventListener('keydown', off, true);
        };
        // Slight delay so the *originating* click on the pill doesn't
        // instantly dismiss the pulse it just started. 250ms is
        // enough to absorb any late-firing synthetic click on touch
        // devices (which can emit a click event ~300ms after a tap)
        // while still feeling responsive to the user's next action.
        setTimeout(() => {
          document.addEventListener('click', off, true);
          document.addEventListener('wheel', off, true);
          document.addEventListener('keydown', off, true);
          dismissRef.current = off;
        }, 250);
      }, 550);

      return () => clearTimeout(t);
    };

    const handler = (e) => focusTicker(e?.detail?.ticker);
    window.addEventListener('squeeze-radar:focus', handler);
    return () => {
      window.removeEventListener('squeeze-radar:focus', handler);
      clearPulse();
    };
  }, []);
  return (
    <div
      ref={rootRef}
      className="rounded-lg border border-amber-500/50 dark:border-amber-400/45 bg-card/80 px-2.5 py-2 shadow-[0_4px_12px_-2px_rgba(15,23,42,0.14),0_2px_4px_-1px_rgba(15,23,42,0.06)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_2px_8px_-2px_rgba(0,0,0,0.6)]"
      data-testid="squeeze-radar-strip"
    >
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-x-3 gap-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Zap className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs uppercase tracking-[0.14em] font-bold text-amber-700 dark:text-amber-500">
            Squeeze Radar
          </span>
          <Link
            to="/learn/short-squeeze-dynamics"
            className="text-muted-foreground/70 hover:text-foreground"
            title="Learn how short squeezes work"
            data-testid="squeeze-radar-help"
          >
            <HelpCircle className="w-3 h-3" />
          </Link>
          {/* Jul 7, 2026 — Verdict-dot legend. Compact, inline with
              the title so users don't have to memorize the color code.
              Order mirrors severity: hot → cool. */}
          <span
            className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-xs text-muted-foreground/90 ml-1 sm:ml-2"
            data-testid="squeeze-radar-legend"
          >
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-rose-500" /> Extreme
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Elevated
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-sky-500" /> Moderate
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-slate-400 dark:bg-slate-500" /> Low
            </span>
          </span>
        </div>
        <span className="text-xs text-muted-foreground/70 inline-flex items-center gap-2">
          Refreshed {fmtAgeShort(data.generated_at)} · {rows.length} candidate{rows.length === 1 ? '' : 's'}
          {/* Round 104g — free users see dividend candidates only; the
              hidden non-dividend count doubles as a Plus upsell hook. */}
          {data.restricted && (data.hidden_non_dividend || 0) > 0 && (
            <button
              type="button"
              onClick={() => setShowUpsell(true)}
              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/30 hover:bg-amber-500/25 transition-colors"
              title="Non-dividend squeeze candidates are a DiviDrip Plus feature"
              data-testid="squeeze-radar-plus-chip"
            >
              <Lock className="w-3 h-3" />
              +{data.hidden_non_dividend} non-dividend · Plus
            </button>
          )}
        </span>
      </div>
      {/* Round 102e (Feb 2026) — Sort-order toggle, tucked under the
          title. Dark-orange ▲ "Most Extreme" is the default (high →
          low); one click flips to sky-blue ▼ "Less Extreme" (low →
          high). Both label + icon share the same colour so the state
          is obvious at a glance. */}
      {rows.length > 1 && (
        <div className="mb-1.5">
          <button
            type="button"
            onClick={() => setSortDesc((prev) => !prev)}
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ring-1 transition-colors ${sortDesc ? 'text-orange-700 dark:text-orange-400 ring-orange-400/50 bg-orange-50/60 dark:bg-orange-950/30 hover:bg-orange-100/60 dark:hover:bg-orange-950/50' : 'text-sky-700 dark:text-sky-400 ring-sky-400/50 bg-sky-50/60 dark:bg-sky-950/30 hover:bg-sky-100/60 dark:hover:bg-sky-950/50'}`}
            title={sortDesc ? 'Currently sorted Extreme → Low. Click to reverse.' : 'Currently sorted Low → Extreme. Click to reverse.'}
            data-testid="squeeze-radar-sort-toggle"
          >
            {sortDesc ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            <span>{sortDesc ? '⚠ Most Extreme' : 'Less Extreme'}</span>
          </button>
        </div>
      )}
      {/* `overflow-y-visible` so the Squeeze Radar focus-pulse's outer
          box-shadow bloom isn't clipped by the horizontal scroller.
          Added a hair of vertical padding + neg-margin so the pulse
          has room to breathe without shifting layout. */}
      <div ref={swipeRef} onScroll={updateSwipeHint} className="flex items-stretch gap-1.5 overflow-x-auto overflow-y-visible pb-2 -mx-1 -my-1 px-1 py-1 scrollbar-thin">
        {orderedRows.map((r) => {
          const verdictCls = VERDICT_STYLES[r.verdict] || VERDICT_STYLES.Low;
          const dotCls = VERDICT_DOTS[r.verdict] || VERDICT_DOTS.Low;
          const live = livePrices[r.ticker] || {};
          // Prefer live SSE price; fall back to the snapshot captured
          // at cron time if the stream hasn't produced a value yet.
          const currentPrice = typeof live.price === 'number' ? live.price : (typeof r.price === 'number' ? r.price : null);
          const priceStr = currentPrice != null ? `$${currentPrice.toFixed(2)}` : '—';
          const isLive = typeof live.price === 'number';
          // Intraday % change (only meaningful during regular hours).
          const changePct = typeof live.change === 'number' ? live.change : null;
          const showBadge = marketOpen && changePct != null;
          const changeUp = showBadge && changePct >= 0;
          const changeDown = showBadge && changePct < 0;
          // Squeeze already ripping today — flame overlay to warn
          // "you may have missed the entry" while the nightly SI data
          // is still stale.
          const isMoving = marketOpen && changePct != null && Math.abs(changePct) >= SQUEEZE_MOVING_THRESHOLD_PCT;
          const priceLabel = marketOpen ? 'Current Price' : 'Last Close';
          return (
            <button
              key={r.ticker}
              type="button"
              onClick={() => onOpenStock && onOpenStock(r.ticker, 'stock-metrics')}
              className={`shrink-0 flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-md border ${verdictCls} hover:bg-sky-100 dark:hover:bg-sky-900/60 transition-colors cursor-pointer`}
              data-testid={`squeeze-radar-chip-${r.ticker}`}
              title={`${r.ticker} — Short interest ${r.si_pct_float}% of float, ${r.days_to_cover} days to cover. Verdict: ${r.verdict}.${isMoving ? ` Moving ${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}% intraday — squeeze may be playing out NOW.` : ''} Click to open the Stock Modal.`}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${dotCls}`}
                  aria-label={`Verdict: ${r.verdict}`}
                  data-testid={`squeeze-radar-dot-${r.ticker}`}
                />
                <span className="font-mono font-bold text-sm text-foreground">{r.ticker}</span>
                <span className="font-mono text-xs">{r.si_pct_float.toFixed(1)}% SI</span>
                <span className="text-xs text-muted-foreground/80">·</span>
                <span className="font-mono text-xs">{r.days_to_cover.toFixed(1)}d cover</span>
                {isMoving && (
                  <Flame
                    className="w-3.5 h-3.5 text-orange-500 animate-pulse"
                    title={`Moving ${changePct >= 0 ? '+' : ''}${changePct.toFixed(1)}% intraday — squeeze in progress`}
                    data-testid={`squeeze-radar-flame-${r.ticker}`}
                  />
                )}
              </div>
              <div className="flex items-baseline gap-1 pl-3.5 flex-wrap">
                <span
                  className="text-[10px] uppercase tracking-wide text-muted-foreground/80"
                  data-testid={`squeeze-radar-current-price-label-${r.ticker}`}
                >
                  {priceLabel}
                </span>
                <span
                  className="font-mono text-xs font-semibold text-foreground"
                  data-testid={`squeeze-radar-current-price-${r.ticker}`}
                >
                  {priceStr}
                </span>
                {showBadge && (
                  <span
                    className={`inline-flex items-center gap-0.5 font-mono text-xs font-semibold ${changeUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
                    data-testid={`squeeze-radar-change-${r.ticker}`}
                  >
                    {changeUp ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(changePct).toFixed(2)}%
                  </span>
                )}
                {isLive && marketOpen && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
                    title="Live-streaming price"
                    aria-label="Live price"
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
      <SwipeHint hint={swipeHint} testId="squeeze-radar-swipe-hint" />
      <PlusUpsellModal open={showUpsell} onClose={() => setShowUpsell(false)} />
    </div>
  );
}
